import {
  AcExpoEventListType,
  ActivateCashRegisterResponse,
  CashRegisterHolder,
  CreateEventsFromCsvPost,
  EventType,
  getDaysInRange,
  PaginatedResponse,
  RemainingReportsCount,
  SetOrganiserGainsBody,
  UpdateEventInterlocutorBody,
  UpdateMaxEntryNotificationsBody,
  UpdatePaymentMethodBody,
} from "@ac-expo/api-types";
import { toDatabaseFormat } from "@ac-expo/utils";
import { Day, Event, EventReport, EventStatus, EventTimetableState } from "@prisma/client";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { handleApiCall } from "../../api-error-handler";
import { privateAxios, publicAxios } from "../../axiosInstances";
import { potentialDuplicateEventsQueryKeys, ticketTypesQueryKeys } from "../../query-keys";
import eventsQueryKeys, {
  acExpoEventsQueryKeys,
  cashRegisterHoldersQueryKeys,
  cashRegisterSessionsQueryKeys,
  likeEventsQueryKeys,
} from "../../query-keys/events.query-keys";
import {
  AddEventAsVisitorForm,
  AddFeaturesForm,
  CreateEventForm,
  CustomEventAppearance,
  defaultEventDateRangeValues,
  DuplicateEventForm,
  EventDateRangeForm,
  EventReportForm,
  ModifyEventForm,
  TransferEventAcExpoForm,
  TransferEventForm,
  UpdateContactPhoneForm,
  UpdateEventForm,
  UpsertCustomPosterForm,
} from "../../schemas";
import {
  CashRegisterCustomBlocksForm,
  CashRegisterHolderForm,
} from "../../schemas/events/cash-register.schema";

type CreateEventMutationVariables = CreateEventForm & { isAccepted?: boolean };

type CreateEventPayload = Omit<CreateEventMutationVariables, "dates"> & {
  dates: {
    schedules: Array<{
      fromDate: string;
      toDate: string;
      timetables: Array<{
        day: Day;
        state: EventTimetableState;
        openingTime?: string | null;
        closingTime?: string | null;
        breakStartTime?: string | null;
        breakEndTime?: string | null;
      }>;
    }>;
    exceptionalClosingDates: Array<{ date: string }>;
    isContinuous: boolean;
  };
};

type CreateEventFormWithStringDates = Omit<CreateEventForm, "dates"> & {
  dates: {
    schedules: ({
      fromDate: string;
      toDate: string;
    } & Pick<EventDateRangeForm, "timetables">)[];
    exceptionalClosingDates: {
      date: string;
    }[];
    isContinuous: boolean;
  };
};

// createEvent
export const createEvent = async (body: CreateEventFormWithStringDates) => {
  const { data } = await privateAxios.post<EventType>("/events", body);
  return data;
};

export const useCreateEventMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createEvent,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: eventsQueryKeys.all,
      });
    },
  });
};

export const duplicateEvent = async (body: DuplicateEventForm) => {
  try {
    const usefulDays = getDaysInRange(body.dates.from, body.dates.to);
    const eventTimetables = defaultEventDateRangeValues.timetables.map((timetable) => ({
      ...timetable,
      state: usefulDays.includes(timetable.day) ? timetable.state : "closed",
    }));
    const { data } = await privateAxios.post<EventType>(`/events/${body.id}/duplicate`, {
      ...body,
      dates: {
        schedules: [
          {
            fromDate: toDatabaseFormat(body.dates.from),
            toDate: toDatabaseFormat(body.dates.to),
            timetables: eventTimetables,
          },
        ],
        exceptionalClosingDates: [],
        isContinuous: false,
      },
    });
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      error.message = error.response?.data.message;
    }
    throw error;
  }
};

export const useDuplicateEventMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: duplicateEvent,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: eventsQueryKeys.all,
      });
    },
  });
};

interface CreateEventAsVisitorPayload {
  eventWebsite?: string;
  dates: {
    schedules: Array<{
      fromDate: string;
      toDate: string;
      timetables: Array<{
        day: Day;
        state: EventTimetableState;
        openingTime?: string;
        closingTime?: string;
      }>;
    }>;
    exceptionalClosingDates: Array<{ date: string }>;
    isContinuous: boolean;
  };
  [key: string]: unknown;
}

const DAY_OF_WEEK_MAP: Record<number, Day> = {
  0: "sunday",
  1: "monday",
  2: "tuesday",
  3: "wednesday",
  4: "thursday",
  5: "friday",
  6: "saturday",
};

const ALL_DAYS: Day[] = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

const createEventAsVisitor = async (data: CreateEventAsVisitorPayload): Promise<EventType> => {
  try {
    const res = await privateAxios.post<EventType>("/events/create-event-as-visitor", data);
    return res.data;
  } catch (error) {
    if (isAxiosError(error)) {
      error.message = error.response?.data.message ?? error.message;
    }
    throw error;
  }
};

function buildTimetablesFromTimes(
  times: AddEventAsVisitorForm["dates"]["times"],
): CreateEventAsVisitorPayload["dates"]["schedules"][0]["timetables"] {
  const timesMap = new Map<Day, { start: string; end: string }>();

  times.forEach((time) => {
    const date = new Date(time.day);
    const day = DAY_OF_WEEK_MAP[date.getDay()];
    timesMap.set(day, { start: time.start, end: time.end });
  });

  return ALL_DAYS.map((day) => {
    const time = timesMap.get(day);
    if (time) {
      return {
        day,
        state: "opened" as EventTimetableState,
        openingTime: time.start,
        closingTime: time.end,
      };
    }
    return {
      day,
      state: "closed" as EventTimetableState,
    };
  });
}

export const useCreateEventAsVisitor = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: AddEventAsVisitorForm) => {
      const { dates, websiteUrl, ...other } = data;

      const timetables = buildTimetablesFromTimes(dates.times);

      const formattedData: CreateEventAsVisitorPayload = {
        ...other,
        ...(websiteUrl && { eventWebsite: websiteUrl }),
        dates: {
          schedules: [
            {
              fromDate: toDatabaseFormat(dates.from),
              toDate: toDatabaseFormat(dates.to),
              timetables,
            },
          ],
          exceptionalClosingDates: [],
          isContinuous: false,
        },
      };

      return createEventAsVisitor(formattedData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: eventsQueryKeys.all,
      });
    },
  });
};

// modifyEvent
export const modifyEvent = async (body: ModifyEventForm) => {
  try {
    const { data } = await privateAxios.patch("/events", body);
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      error.message = error.response?.data.message;
    }
    throw error;
  }
};

export const useModifyEventMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: modifyEvent,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: eventsQueryKeys.all,
      });
    },
  });
};

type UpdateEventBody = UpdateEventForm & {
  areExhibitorsOnline?: boolean;
  isProgramOnline?: boolean;
};

export const updateEventCustomAppearance = async (body: CustomEventAppearance) => {
  try {
    const { data } = await privateAxios.patch(`/events/${body.id}/custom-appearance`, body);
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw {
        ...error,
        message: error.response?.data.message,
      };
    }
    throw error;
  }
};

export const useUpdateEventCustomAppearanceMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateEventCustomAppearance,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: eventsQueryKeys.all,
      });
      queryClient.invalidateQueries({
        queryKey: acExpoEventsQueryKeys.all,
      });
    },
  });
};

export const updateEvent = async (body: UpdateEventBody) => {
  try {
    const { data } = await privateAxios.patch(`/events/${body.id}`, {
      isFromAi: false,
      ...body,
      dates: {
        schedules: body.dates.schedules.map((schedule) => ({
          ...schedule,
          fromDate: toDatabaseFormat(schedule.dates?.from ?? new Date()),
          toDate: toDatabaseFormat(schedule.dates?.to ?? new Date()),
          timetables: schedule.timetables.map((timetable) => ({
            ...timetable,
            openingTime: timetable.openingTime?.toString().slice(0, 5),
            closingTime: timetable.closingTime?.toString().slice(0, 5),
            breakStartTime: timetable.breakStartTime?.toString().slice(0, 5),
            breakEndTime: timetable.breakEndTime?.toString().slice(0, 5),
          })),
        })),
        exceptionalClosingDates: body.dates.exceptionalClosingDates.map((schedule) => ({
          ...schedule,
          date: toDatabaseFormat(schedule.date),
        })),
        isContinuous: body.dates.isContinuous,
      },
    });
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      error.message = error.response?.data.message;
    }
    throw error;
  }
};

export const useUpdateEventMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateEvent,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: eventsQueryKeys.all,
      });
      queryClient.invalidateQueries({
        queryKey: acExpoEventsQueryKeys.all,
      });
    },
  });
};

type UpdateEventSimpleBody = Partial<
  Pick<
    UpdateEventBody,
    | "openTicketingWhenOnline"
    | "activateBadgesWhenOnline"
    | "setExhibitorsOnlineWhenOnline"
    | "areExhibitorsOnline"
    | "setProgramOnlineWhenOnline"
    | "isProgramOnline"
    | "description"
    | "websiteUrl"
    | "linkedinUrl"
    | "facebookUrl"
    | "twitterUrl"
    | "instagramUrl"
    | "youtubeUrl"
  >
> & {
  areBadgesOnline?: boolean;
  showTicketNameOnScan?: boolean;
};

export const updateEventSimple = async ({
  eventId,
  body,
}: {
  eventId: string;
  body: UpdateEventSimpleBody;
}) => {
  try {
    const { data } = await privateAxios.patch<Event>(`/events/${eventId}/simple`, body);

    return data;
  } catch (error) {
    if (isAxiosError(error)) error.message = error.response?.data.message;
    throw error;
  }
};

export const useUpdateEventSimpleMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateEventSimple,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: eventsQueryKeys.all,
      });
      queryClient.invalidateQueries({
        queryKey: acExpoEventsQueryKeys.all,
      });
    },
  });
};

type UpdateEventFeaturesBody = Pick<
  AddFeaturesForm,
  "isTicketingEnabled" | "accessControlAccounts"
>;

const updateEventFeatures = async (eventId: string, body: UpdateEventFeaturesBody) => {
  const { data } = await privateAxios.patch(`/events/${eventId}/features`, body);
  return data;
};

export const useUpdateEventFeaturesMutation = (eventId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: UpdateEventFeaturesBody) => updateEventFeatures(eventId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: eventsQueryKeys.get(eventId),
      });
    },
  });
};

type SetAvailableBody = {
  isAvailable: boolean;
  openTicketingWhenOnline: boolean;
  activateBadgesWhenOnline: boolean;
};

const setAvailable = async (eventId: string, body: SetAvailableBody) => {
  try {
    const { data } = await privateAxios.patch(`/events/${eventId}/available`, body);
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      error.message = error.response?.data.message;
    }
    throw error;
  }
};

export const useSetAvailableEventMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      eventId,
      ...body
    }: {
      eventId: string;
    } & SetAvailableBody) => setAvailable(eventId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: eventsQueryKeys.all,
      });
    },
  });
};

export const deleteEvent = async (id: string) => {
  try {
    const { data } = await privateAxios.delete(`/events/${id}`);
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      error.message = error.response?.data.message;
    }
    throw error;
  }
};

export const useDeleteEventMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteEvent(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: eventsQueryKeys.all,
      });
    },
  });
};

const purchaseVigils = async (eventId: string, count: number) => {
  const { data } = await privateAxios.patch(`/events/${eventId}/purchase-inspectors`, {
    count,
  });
  return data;
};

export const usePurchaseVigilsMutation = (eventId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ count }: { count: number }) => purchaseVigils(eventId, count),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: eventsQueryKeys.get(eventId),
      });
    },
  });
};

interface UpdateEventInspectorIsActiveMutationArgs {
  eventInspectorUserId: string;
  isActive: boolean;
}

const updateEventInspectorIsActive = async ({
  eventInspectorUserId,
  isActive,
}: UpdateEventInspectorIsActiveMutationArgs) => {
  const { data } = await privateAxios.patch(`/events/inspectors/${eventInspectorUserId}/active`, {
    isActive,
  });
  return data;
};

export const useUpdateEventInspectorIsActiveMutation = (eventId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (args: UpdateEventInspectorIsActiveMutationArgs) =>
      updateEventInspectorIsActive(args),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: eventsQueryKeys.get(eventId),
      });
    },
  });
};

const deleteEventInspector = async (id: string) => {
  const { data } = await privateAxios.delete(`/events/inspectors/${id}`);
  return data;
};

export const useDeleteEventInspectorMutation = (eventId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteEventInspector,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: eventsQueryKeys.get(eventId),
      });
    },
  });
};

const updateContactPhone = async (eventId: string, body: UpdateContactPhoneForm) => {
  const { data } = await privateAxios.patch(`/events/${eventId}/contact-phone`, body);
  return data;
};

export const useUpdateContactPhoneMutation = (eventId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: UpdateContactPhoneForm) => updateContactPhone(eventId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: eventsQueryKeys.get(eventId),
      });
    },
  });
};

const transferEvent = async (values: TransferEventForm) => {
  const { eventId, connectId } = values;
  const { data: response } = await privateAxios.post(`/events/${eventId}/transfer`, { connectId });
  return response;
};

export const useTransferEventMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (values: TransferEventForm) => transferEvent(values),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: eventsQueryKeys.me(),
      });
    },
  });
};

const transferEventAcExpo = async (values: TransferEventAcExpoForm) => {
  const { data } = await privateAxios.post("/events/transfer-ac-expo", values);
  return data;
};

export const useTransferEventAcExpoMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (values: TransferEventAcExpoForm) => transferEventAcExpo(values),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: eventsQueryKeys.me(),
      });
    },
  });
};

const updateEventStatus = (eventId: string, status: EventStatus) =>
  handleApiCall(() =>
    privateAxios.post<{ status: EventStatus }>(`/events/${eventId}/update-status`, {
      status,
    }),
  );

export const useUpdateEventStatusMutation = (
  eventId: string,
  shouldTemporaryRemoveEvent?: boolean,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (status: EventStatus) => updateEventStatus(eventId, status),
    onSuccess: ({ status }) => {
      queryClient.setQueriesData(
        { queryKey: acExpoEventsQueryKeys.all },
        (oldData: PaginatedResponse<AcExpoEventListType> | undefined) => {
          if (!oldData?.data) return oldData;

          if (!shouldTemporaryRemoveEvent) {
            return {
              ...oldData,
              data: oldData.data.map((event) =>
                event.id === eventId ? { ...event, status } : event,
              ),
            };
          }

          return {
            ...oldData,
            data: oldData.data.filter((event) => event.id !== eventId),
            count: Math.max(0, oldData.count - 1),
          };
        },
      );

      queryClient.invalidateQueries({
        queryKey: acExpoEventsQueryKeys.all,
      });
      queryClient.invalidateQueries({
        queryKey: potentialDuplicateEventsQueryKeys.all,
      });
    },
  });
};

const createEventFromCsv = async (data: CreateEventsFromCsvPost) => {
  const { data: response } = await privateAxios.post(`/events/create-event-from-csv`, data);
  return response;
};

export const useCreateEventFromCsv = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createEventFromCsv,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: acExpoEventsQueryKeys.all,
      });
    },
  });
};

const ignoreEventReport = async (eventReportId: string) => {
  const { data } = await privateAxios.post<RemainingReportsCount>(
    `/events/reported/${eventReportId}`,
  );
  return data;
};

export const useIgnoreEventReportMutation = (eventId: string, eventReportId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => ignoreEventReport(eventReportId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: acExpoEventsQueryKeys.all,
      });
      queryClient.invalidateQueries({
        queryKey: eventsQueryKeys.get(eventId),
      });
    },
  });
};

const newEventReport = async (eventId: string, data: EventReportForm) => {
  const { data: eventReport } = await privateAxios.post<EventReport>(
    `/events/reported/new/${eventId}`,
    data,
  );
  return eventReport;
};

export const useNewEventReportMutation = (eventId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: EventReportForm) => newEventReport(eventId, data),
    onSuccess: ({ eventId }) => {
      queryClient.invalidateQueries({
        queryKey: eventsQueryKeys.get(eventId),
      });
    },
  });
};

const updateOrganiserEmailFromAiControlledEvent = async (
  eventId: string,
  data: { email: string },
) => {
  const { data: response } = await privateAxios.patch(`events/pending/${eventId}`, data);
  return response;
};

export const useUpdateOrganiserEmail = (eventId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { email: string }) =>
      updateOrganiserEmailFromAiControlledEvent(eventId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: acExpoEventsQueryKeys.list(),
      });
    },
  });
};

const updateAiPendingEvent = async (eventId: string | undefined, isAccepted: boolean) => {
  const { data: response } = await privateAxios.post<{ isDraft: boolean }>(
    `events/pending/${eventId}`,
    {
      isAccepted,
    },
  );
  return response;
};

export const useUpdateAiPendingEvent = (eventId: string | undefined) => {
  return useMutation({
    mutationFn: (isAccepted: boolean) => updateAiPendingEvent(eventId, isAccepted),
  });
};

const updateExhibitorContactPhone = async (eventId: string, body: UpdateContactPhoneForm) => {
  const { data } = await privateAxios.patch(`/events/${eventId}/exhibitor-contact-phone`, body);
  return data;
};

export const useUpdateExhibitorContactPhoneMutation = (eventId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: UpdateContactPhoneForm) => updateExhibitorContactPhone(eventId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: eventsQueryKeys.get(eventId),
      });
    },
  });
};

export const registerEventDetailsView = (eventId: string) =>
  handleApiCall(() => publicAxios.post(`/events/public/${eventId}/details-view`));

const verifyEventPassword = async (eventId: string, password: string) => {
  const { data } = await publicAxios.post<{ token: string }>(
    `/events/public/${eventId}/verify-password`,
    { password },
  );
  return data;
};

export const useVerifyEventPasswordMutation = () => {
  return useMutation({
    mutationFn: ({ eventId, password }: { eventId: string; password: string }) =>
      verifyEventPassword(eventId, password),
  });
};

async function likeEvent(eventId: string, like: boolean) {
  try {
    const { data } = await privateAxios.post(`/events/like`, {
      eventId,
      like,
    });
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw new Error(error.response?.data.message || error.message);
    }
    throw error;
  }
}

export function useLikeEventMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ eventId, like }: { eventId: string; like: boolean }) => likeEvent(eventId, like),
    onSuccess: (resultData, { eventId, like }) => {
      queryClient.invalidateQueries({
        queryKey: likeEventsQueryKeys.likes,
      });
    },
  });
}

const createCashRegisterHolder = async (eventId: string, body: CashRegisterHolderForm) => {
  const { data } = await privateAxios.post<CashRegisterHolder>(
    `/events/${eventId}/cash-register/holders`,
    body,
  );
  return data;
};

export const useCreateCashRegisterHolder = (eventId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CashRegisterHolderForm) => createCashRegisterHolder(eventId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: cashRegisterHoldersQueryKeys.byEvent(eventId),
      });
    },
  });
};

const updateCashRegisterHolder = async (
  eventId: string,
  holderId: string,
  body: CashRegisterHolderForm,
) => {
  const { data } = await privateAxios.patch<CashRegisterHolder>(
    `/events/${eventId}/cash-register/holders/${holderId}`,
    body,
  );
  return data;
};

export const useUpdateCashRegisterHolder = (eventId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { holderId: string; data: CashRegisterHolderForm }) =>
      updateCashRegisterHolder(eventId, body.holderId, body.data),
    onSuccess: (variables) => {
      queryClient.invalidateQueries({
        queryKey: cashRegisterHoldersQueryKeys.byEvent(eventId),
      });
    },
  });
};

const updateCashRegisterCustomBlocks = async (
  eventId: string,
  body: CashRegisterCustomBlocksForm,
) => {
  const { data } = await privateAxios.patch(`/events/${eventId}/cash-register/custom/blocks`, body);
  return data;
};

export const useUpdateCashRegisterCustomBlocks = (eventId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CashRegisterCustomBlocksForm) =>
      updateCashRegisterCustomBlocks(eventId, body),
    onSuccess: (variables) => {
      queryClient.invalidateQueries({
        queryKey: ticketTypesQueryKeys.event(eventId),
      });
    },
  });
};

const deleteCashRegisterHolder = async (eventId: string, holderId: string) => {
  const { data } = await privateAxios.delete(
    `/events/${eventId}/cash-register/holders/${holderId}`,
  );
  return data;
};

export const useDeleteCashRegisterHolder = (eventId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (holderId: string) => deleteCashRegisterHolder(eventId, holderId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: cashRegisterHoldersQueryKeys.byEvent(eventId),
      });
    },
  });
};

const deleteCashRegisterCommand = async (eventId: string, commandId: string) => {
  const { data } = await privateAxios.delete(
    `/events/${eventId}/cash-register/command/${commandId}`,
  );
  return data;
};

export const useDeleteCashRegisterCommand = (eventId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (commandId: string) => deleteCashRegisterCommand(eventId, commandId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: cashRegisterSessionsQueryKeys.byEvent(eventId),
      });
    },
  });
};

const reactivateCashRegisterCommand = async (eventId: string, commandId: string) => {
  const { data } = await privateAxios.patch(
    `/events/${eventId}/cash-register/command/${commandId}/reactivate`,
  );
  return data;
};

export const useReactivateCashRegisterCommand = (eventId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (commandId: string) => reactivateCashRegisterCommand(eventId, commandId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: cashRegisterSessionsQueryKeys.byEvent(eventId),
      });
    },
  });
};

const setCashRegisterHolderIsActive = async (
  eventId: string,
  holderId: string,
  isActive: boolean,
) => {
  const { data } = await privateAxios.put<CashRegisterHolder>(
    `/events/${eventId}/cash-register/holders/${holderId}/active/${isActive ? "true" : "false"}`,
  );
  return data;
};

export const useSetCashRegisterHolderIsActive = (eventId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { holderId: string; isActive: boolean }) =>
      setCashRegisterHolderIsActive(eventId, body.holderId, body.isActive),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: cashRegisterHoldersQueryKeys.byEvent(eventId),
      });
    },
  });
};

const setCashRegisterSessionLiquidGain = async (
  eventId: string,
  sessionId: string,
  price: number,
) => {
  const { data } = await privateAxios.put<CashRegisterHolder>(
    `/events/${eventId}/cash-register/sessions/${sessionId}/organiser-liquid-gain`,
    {
      value: price,
    },
  );
  return data;
};

export const useSetCashRegisterSessionLiquidGain = (eventId: string, sessionId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (price: number) => setCashRegisterSessionLiquidGain(eventId, sessionId, price),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: cashRegisterSessionsQueryKeys.byEventAndId(eventId, sessionId),
      });
    },
  });
};

const setCashRegisterSessionControlledAmount = async (
  eventId: string,
  sessionId: string,
  value: number | null,
) => {
  const { data } = await privateAxios.put(
    `/events/${eventId}/cash-register/sessions/${sessionId}/controlled-amount`,
    { value },
  );
  return data;
};

export const useSetCashRegisterSessionControlledAmount = (eventId: string, sessionId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (value: number | null) =>
      setCashRegisterSessionControlledAmount(eventId, sessionId, value),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: cashRegisterSessionsQueryKeys.byEventAndId(eventId, sessionId),
      });
    },
  });
};

const setCashRegisterSessionOrganiserGains = async (
  eventId: string,
  sessionId: string,
  body: SetOrganiserGainsBody,
) => {
  const { data } = await privateAxios.put(
    `/events/${eventId}/cash-register/sessions/${sessionId}/organiser-gains`,
    body,
  );
  return data;
};

export const useSetCashRegisterSessionOrganiserGains = (eventId: string, sessionId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: SetOrganiserGainsBody) =>
      setCashRegisterSessionOrganiserGains(eventId, sessionId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: cashRegisterSessionsQueryKeys.byEventAndId(eventId, sessionId),
      });
    },
  });
};

const updateCashRegistersEntries = async (eventId: string, availableEntriesIds: string[]) => {
  try {
    const { data } = await privateAxios.put(`/events/${eventId}/entries`, {
      ids: availableEntriesIds,
    });
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      error.message = error.response?.data.message;
    }
    throw error;
  }
};

export const useUpdateTicketTypesAvailableOnCashRegisters = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      eventId,
      availableEntriesIds,
    }: {
      eventId: string;
      availableEntriesIds: string[];
    }) => updateCashRegistersEntries(eventId, availableEntriesIds),
    onSuccess: (_: unknown, { eventId }: { eventId: string; availableEntriesIds: string[] }) => {
      queryClient.invalidateQueries({
        queryKey: eventsQueryKeys.entries(eventId),
      });
    },
  });
};

const upsertCustomPoster = async (eventId: string, data: UpsertCustomPosterForm) => {
  const { data: response } = await privateAxios.put<Event>(
    `/events/${eventId}/custom-poster`,
    data,
  );
  return response;
};

export const useUpsertCustomPoster = (eventId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpsertCustomPosterForm) => upsertCustomPoster(eventId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: eventsQueryKeys.get(eventId),
      });
    },
  });
};

const activateCashRegister = async (eventId: string) => {
  try {
    const { data } = await privateAxios.post<ActivateCashRegisterResponse>(
      `/events/${eventId}/cash-register/activate`,
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      error.message = error.response?.data.message;
    }
    throw error;
  }
};

export const useActivateCashRegister = (eventId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => activateCashRegister(eventId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: eventsQueryKeys.get(eventId),
      });
      queryClient.invalidateQueries({
        queryKey: eventsQueryKeys.cashRegisters(eventId),
      });
    },
  });
};

const updateEventInterlocutor = async (eventId: string, data: UpdateEventInterlocutorBody) => {
  try {
    const { data: response } = await privateAxios.patch(`/events/${eventId}/interlocutor`, data);
    return response;
  } catch (error) {
    if (isAxiosError(error)) {
      error.message = error.response?.data.message;
    }
    throw error;
  }
};

export const useUpdateEventInterlocutorMutation = (eventId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateEventInterlocutorBody) => updateEventInterlocutor(eventId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: eventsQueryKeys.get(eventId),
      });
      queryClient.invalidateQueries({
        queryKey: acExpoEventsQueryKeys.all,
      });
    },
  });
};

const updateCashRegisterPaymentMethod = async (
  eventId: string,
  orderId: string,
  body: UpdatePaymentMethodBody,
) => {
  try {
    const { data } = await privateAxios.patch(
      `/events/${eventId}/cash-register/command/${orderId}/payment-method`,
      body,
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      error.message = error.response?.data.message;
    }
    throw error;
  }
};

export const useUpdateCashRegisterPaymentMethod = (eventId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ orderId, body }: { orderId: string; body: UpdatePaymentMethodBody }) =>
      updateCashRegisterPaymentMethod(eventId, orderId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: cashRegisterSessionsQueryKeys.byEvent(eventId),
      });
    },
  });
};

const toggleEntryNotification = async (eventId: string, userId: string) => {
  try {
    const { data } = await privateAxios.post<{ isSubscribed: boolean }>(
      `/events/${eventId}/entry-notification/toggle`,
      { userId },
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      error.message = error.response?.data.message;
    }
    throw error;
  }
};

export const useToggleEntryNotificationMutation = (eventId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => toggleEntryNotification(eventId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: eventsQueryKeys.proVisitors(eventId),
      });
    },
  });
};

const notifyVisitorByEmail = async (eventId: string, userId: string) => {
  try {
    const { data } = await privateAxios.post<{ success: boolean }>(
      `/events/${eventId}/visitor/notify-email`,
      { userId },
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      error.message = error.response?.data.message;
    }
    throw error;
  }
};

export const useNotifyVisitorByEmailMutation = (eventId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => notifyVisitorByEmail(eventId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: eventsQueryKeys.proVisitors(eventId),
      });
    },
  });
};

const updateMaxEntryNotifications = (
  eventId: string,
  data: UpdateMaxEntryNotificationsBody
) =>
  privateAxios
    .patch(`/events/${eventId}/max-entry-notifications`, data)
    .then((res) => res.data);

export const useUpdateMaxEntryNotificationsMutation = (eventId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateMaxEntryNotificationsBody) =>
      updateMaxEntryNotifications(eventId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: eventsQueryKeys.get(eventId),
      });
      queryClient.invalidateQueries({
        queryKey: acExpoEventsQueryKeys.all,
      });
    },
  });
};
